/**
 * ReplayLiveAudioPlayer — plays live replay audio frames over the Web Audio API.
 *
 * Each incoming frame carries base64-encoded PCM16 mono audio:
 *   - agent: 24 kHz, 20 ms per frame (960 samples)
 *   - customer: 16 kHz, 20 ms per frame (640 samples)
 *
 * Frames are decoded, resampled to the AudioContext rate, mixed to stereo
 * (agent slightly left, customer slightly right), and scheduled for gapless
 * playback using AudioBufferSourceNode. A small jitter buffer (~200 ms)
 * absorbs network variance before playback begins.
 *
 * Lost frames (seq gaps) are filled with silence so the timeline is never
 * compressed. Already-scheduled source nodes are tracked and can be stopped
 * when dropping frames to catch up.
 */
import { computeGapSilenceSamples, decodePcm16Base64, mixToStereo } from './replay-audio-utils';

const AGENT_SAMPLE_RATE = 24_000;
const CUSTOMER_SAMPLE_RATE = 16_000;
const FRAME_DURATION_S = 0.02; // 20 ms
const JITTER_BUFFER_MS = 200;
const MAX_LATENCY_MS = 2_000;

interface QueuedFrame {
  seq: number;
  agent: string | null;
  customer: string | null;
  at: number;
}

export class ReplayLiveAudioPlayer {
  private ctx: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private nextStartTime = 0;
  private queue: QueuedFrame[] = [];
  private started = false;
  private muted = false;
  private firstFrameAt: number | null = null;
  private droppedLogged = false;
  private lastSeq = -1;
  /** Track scheduled source nodes so we can stop them when dropping frames. */
  private activeSources: AudioBufferSourceNode[] = [];

  /**
   * Must be called inside a user-gesture handler (click) to satisfy autoplay policy.
   * Creates and resumes the AudioContext.
   */
  start(): void {
    if (this.ctx) return;

    const ACtx =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    this.ctx = new ACtx();
    this.gainNode = this.ctx.createGain();
    this.gainNode.gain.value = this.muted ? 0 : 1;
    this.gainNode.connect(this.ctx.destination);
    this.nextStartTime = 0;
    this.started = false;
    this.firstFrameAt = null;
    this.droppedLogged = false;
    this.lastSeq = -1;
    this.activeSources = [];
    this.queue = [];
  }

  /** Stop playback and release the AudioContext. */
  stop(): void {
    // Stop all scheduled source nodes
    for (const src of this.activeSources) {
      try {
        src.stop();
      } catch {
        // Already stopped or not yet started — ignore
      }
    }
    this.activeSources = [];

    if (this.ctx) {
      void this.ctx.close().catch(() => {});
      this.ctx = null;
    }
    this.gainNode = null;
    this.nextStartTime = 0;
    this.started = false;
    this.lastSeq = -1;
    this.queue = [];
  }

  /** Mute/unmute without stopping the audio pipeline. */
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.gainNode) {
      this.gainNode.gain.value = muted ? 0 : 1;
    }
  }

  /** Current latency in ms (how far ahead the next scheduled buffer is from now). */
  latencyMs(): number {
    if (!this.ctx || !this.started) return 0;
    const ahead = this.nextStartTime - this.ctx.currentTime;
    return Math.max(0, ahead * 1000);
  }

  /** Feed a single audio frame from the socket. */
  pushFrame(seq: number, agent: string | null, customer: string | null, at: number): void {
    if (!this.ctx) return;

    // If we haven't started yet, buffer frames for the jitter window
    if (!this.started) {
      this.queue.push({ seq, agent, customer, at });
      if (this.firstFrameAt === null) this.firstFrameAt = performance.now();

      if (performance.now() - this.firstFrameAt >= JITTER_BUFFER_MS) {
        this.started = true;
        // Resume the context (in case it's suspended)
        void this.ctx.resume().catch(() => {});
        this.nextStartTime = this.ctx.currentTime + 0.05; // tiny lead-in
        for (const qf of this.queue) {
          this.handleSeqGap(qf.seq);
          this.scheduleFrame(qf.agent, qf.customer);
          this.lastSeq = qf.seq;
        }
        this.queue = [];
      }
      return;
    }

    // Drop frames if we're too far behind (> MAX_LATENCY_MS)
    const latency = this.latencyMs();
    if (latency > MAX_LATENCY_MS) {
      if (!this.droppedLogged) {
        console.warn('[ReplayLiveAudioPlayer] Dropping frames — queue exceeded 2 s behind');
        this.droppedLogged = true;
      }
      // Stop all already-scheduled source nodes
      for (const src of this.activeSources) {
        try {
          src.stop();
        } catch {
          // ignore
        }
      }
      this.activeSources = [];
      // Reset the schedule to catch up
      this.nextStartTime = this.ctx.currentTime + 0.02;
      this.lastSeq = seq;
      return;
    }

    this.handleSeqGap(seq);
    this.scheduleFrame(agent, customer);
    this.lastSeq = seq;
  }

  /** Insert silence for any gap between lastSeq and the incoming seq. */
  private handleSeqGap(seq: number): void {
    if (this.lastSeq < 0) return; // first frame — no gap possible
    const gap = seq - this.lastSeq - 1;
    if (gap <= 0 || !this.ctx) return;

    const silenceSamples = computeGapSilenceSamples(gap, this.ctx.sampleRate, FRAME_DURATION_S);
    if (silenceSamples > 0) {
      this.nextStartTime += silenceSamples / this.ctx.sampleRate;
    }
  }

  private scheduleFrame(agent: string | null, customer: string | null): void {
    if (!this.ctx || !this.gainNode) return;

    const agentSamples = agent ? decodePcm16Base64(agent) : null;
    const customerSamples = customer ? decodePcm16Base64(customer) : null;

    if (!agentSamples && !customerSamples) {
      // Both silent — advance the timeline by one frame
      this.nextStartTime += FRAME_DURATION_S;
      return;
    }

    const outputRate = this.ctx.sampleRate;
    const { left, right, length } = mixToStereo(
      agentSamples,
      customerSamples,
      outputRate,
      AGENT_SAMPLE_RATE,
      CUSTOMER_SAMPLE_RATE,
    );

    if (length === 0) {
      this.nextStartTime += FRAME_DURATION_S;
      return;
    }

    const buffer = this.ctx.createBuffer(2, length, outputRate);
    buffer.getChannelData(0).set(left);
    buffer.getChannelData(1).set(right);

    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(this.gainNode);
    src.start(this.nextStartTime);

    // Track for cleanup; prune when playback finishes
    this.activeSources.push(src);
    src.onended = () => {
      const idx = this.activeSources.indexOf(src);
      if (idx !== -1) this.activeSources.splice(idx, 1);
    };

    this.nextStartTime += buffer.duration;
  }
}
